import { useState } from "react";
import { motion } from "framer-motion";
import { Dices, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Slider } from "../components/ui/slider";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import BetControl from "../components/BetControl";
import Confetti from "../components/Confetti";
import CoinRain from "../components/CoinRain";
import { formatCoins } from "../lib/format";
import { playSpin, playWin, playLose } from "../lib/sound";

export default function DiceGame() {
  const { t } = useLang();
  const { user, setCoins } = useAuth();
  const [bet, setBet] = useState(100);
  const [direction, setDirection] = useState("over");
  const [target, setTarget] = useState(50);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [confetti, setConfetti] = useState(0);
  const [rain, setRain] = useState(0);

  const winChance = direction === "over" ? 100 - target : target - 1;
  const mult = winChance > 0 ? (0.98 / (winChance / 100)).toFixed(2) : "0";

  const roll = async () => {
    if (busy) return;
    setBusy(true);
    setResult(null);
    playSpin();
    try {
      const { data } = await api.post("/games/dice", { bet, direction, target });
      setCoins(data.coins);
      setResult(data);
      if (data.win > 0) { setConfetti(Date.now()); setRain(Date.now()); playWin(data.win); toast.success(`${t("youWon")} +${formatCoins(data.win)}!`); }
      else { playLose(); toast(`${t("youLost")} (-${formatCoins(bet)})`); }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <Confetti fire={confetti} />
      <CoinRain fire={rain} />
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold gold-text">{t("diceGame")}</h1>
        <p className="text-sm text-slate-400 mt-1">1 – 100 · {t("over")}/{t("under")} {t("target")}</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <div className="mw-card p-6 flex flex-col items-center justify-center min-h-[280px]">
          <motion.div
            key={result ? result.roll : "idle"}
            initial={{ scale: 0.6, rotate: -20, opacity: 0 }}
            animate={{ scale: 1, rotate: 0, opacity: 1 }}
            className="grid place-items-center h-36 w-36 rounded-3xl mw-card glow-cyan"
            data-testid="dice-display"
          >
            <span className="font-display text-6xl font-extrabold" style={{ color: result ? (result.won ? "#00E676" : "#FF3366") : "#00F0FF" }}>
              {result ? result.roll : <Dices size={56} className="text-cyan-300" />}
            </span>
          </motion.div>
          {result && (
            <p className="mt-4 font-display text-xl font-bold" style={{ color: result.won ? "#00E676" : "#FF3366" }} data-testid="dice-result">
              {result.won ? `${t("youWon")} +${formatCoins(result.win)}` : `${t("youLost")} -${formatCoins(bet)}`}
            </p>
          )}
        </div>

        <div className="mw-card p-6 space-y-5">
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => setDirection("under")} disabled={busy} data-testid="dice-under-btn"
              className={`rounded-xl py-3 font-bold transition-colors ${direction === "under" ? "btn-gold" : "border border-amber-500/30 bg-black/30 text-amber-200"}`}>
              ▼ {t("under")}
            </button>
            <button onClick={() => setDirection("over")} disabled={busy} data-testid="dice-over-btn"
              className={`rounded-xl py-3 font-bold transition-colors ${direction === "over" ? "btn-gold" : "border border-amber-500/30 bg-black/30 text-amber-200"}`}>
              ▲ {t("over")}
            </button>
          </div>

          <div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-slate-400">{t("target")}</span>
              <span className="font-mono-num font-bold text-amber-200">{target}</span>
            </div>
            <Slider value={[target]} min={2} max={98} step={1} onValueChange={(v) => setTarget(v[0])} disabled={busy} data-testid="dice-target-slider" />
          </div>

          <div className="grid grid-cols-2 gap-3 text-center">
            <div className="rounded-lg border border-cyan-400/20 bg-black/30 py-2">
              <p className="text-[10px] uppercase text-slate-400">{t("winChance")}</p>
              <p className="font-mono-num font-bold text-cyan-300">{winChance}%</p>
            </div>
            <div className="rounded-lg border border-amber-500/20 bg-black/30 py-2">
              <p className="text-[10px] uppercase text-slate-400">{t("multiplier")}</p>
              <p className="font-mono-num font-bold text-amber-300">{mult}×</p>
            </div>
          </div>

          <BetControl bet={bet} setBet={setBet} max={user?.coins} disabled={busy} />
          <button onClick={roll} disabled={busy} data-testid="dice-roll-btn" className="w-full btn-gold rounded-xl py-3 text-lg flex items-center justify-center gap-2">
            {busy ? <Loader2 className="animate-spin" size={18} /> : <Dices size={18} />} {t("rollDice")}
          </button>
        </div>
      </div>
    </div>
  );
}
